/**
 * Central product configuration for the ThreatLens360 multi-product platform.
 * Each product defines its metadata, navigation items, and routing behavior.
 *
 * SHELL_MAIN_ITEMS  — always-visible top section (Executive Summary, LensGuard AI)
 * SHELL_SYSTEM_ITEMS — always-visible bottom section (Integrations, Settings, Help)
 * product.navItems  — product-specific pages only (no shell items)
 */

// ── Shell-level navigation (rendered by ProductSidebar regardless of active product) ──

export const SHELL_MAIN_ITEMS = [
  { icon: 'LayoutDashboard', label: 'Executive Summary', path: '/new/dashboard' },
  { icon: 'Sparkles',        label: 'LensGuard AI',      path: '/lensguard-ai'  },
];

export const SHELL_SYSTEM_ITEMS = [
  { icon: 'Plug',       label: 'Integrations',  path: '/integrations' },
  { icon: 'Settings',   label: 'Settings',      path: '/settings'     },
  { icon: 'HelpCircle', label: 'Help & Support', path: '/support'      },
];

// ── Per-product configuration ──

export const PRODUCTS = {
  surface: {
    id: 'surface',
    name: 'Surface',
    fullName: 'ThreatLens Surface',
    tagline: 'External Attack Surface Management',
    description: 'Discover, monitor, and secure your entire external attack surface — domains, subdomains, IPs, certificates, and exposed services.',
    color: '#10B981',
    icon: 'Shield',
    defaultRoute: '/new/dashboard',
    comingSoon: false,
    tierRequired: null,
    navItems: [
      { type: 'subsection', label: 'Scope' },
      { icon: 'Globe',    label: 'TLD Scope',         path: '/new/surface/tld-scope'       },
      { icon: 'Globe',    label: 'Digital Footprint', path: '/new/surface/digital-footprint' },
      { type: 'subsection', label: 'Surface Analysis' },
      { icon: 'Globe',    label: 'Attack Surface',    path: '/new/surface/attack-surface'  },
      { icon: 'Server',   label: 'Asset Profiles',    path: '/new/surface/assets/profile'  },
      { icon: 'Lock',     label: 'Vulnerabilities',   path: '/new/surface/vulnerabilities' },
      { icon: 'Route',    label: 'Attack Paths',      path: '/new/surface/attack-paths'    },
    ],
  },
  exposure: {
    id: 'exposure',
    name: 'Exposure Intel',
    fullName: 'ThreatLens Exposure Intelligence',
    tagline: 'Credential, Brand, and Artifact Intelligence',
    description: 'Unify leaked credentials, GitHub/code exposure, dark web signals, brand abuse, artifact intelligence, and threat actor relevance.',
    color: '#F59E0B',
    icon: 'Radio',
    defaultRoute: '/new/exposure/data-exposure',
    comingSoon: false,
    tierRequired: null,
    navItems: [
      { type: 'subsection', label: 'Exposure Intelligence' },
      { icon: 'Droplets', label: 'Data Exposure',       path: '/new/exposure/data-exposure' },
      { icon: 'Key',      label: 'Credential Intel',    path: '/new/exposure/credential-intel' },
      { icon: 'Lock',     label: 'Code Exposure',       path: '/new/exposure/code-exposure' },
      { icon: 'Rss',      label: 'Brand & Fraud',       path: '/new/exposure/brand-fraud' },
      { icon: 'Target',   label: 'Threat Actors',       path: '/new/exposure/threat-actors' },
    ],
  },
  risk: {
    id: 'risk',
    name: 'Risk & Compliance',
    fullName: 'ThreatLens Risk & Compliance',
    tagline: 'Cyber Rating, Financial Exposure, and Compliance',
    description: 'Translate external exposure into board-ready cyber ratings, financial impact, compliance controls, benchmarks, and reports.',
    color: '#EF4444',
    icon: 'TrendingUp',
    defaultRoute: '/new/risk/cyber-rating',
    comingSoon: false,
    tierRequired: null,
    navItems: [
      { type: 'subsection', label: 'Risk Decisions' },
      { icon: 'TrendingUp', label: 'Cyber Rating',        path: '/new/risk/cyber-rating' },
      { icon: 'Target',     label: 'Prioritization',      path: '/new/risk/prioritization' },
      { icon: 'ShieldAlert', label: 'Financial Impact',   path: '/new/risk/financial-impact' },
      { icon: 'Lock',       label: 'Compliance Mapping',  path: '/new/risk/compliance' },
      { icon: 'Rss',        label: 'Reports',             path: '/new/risk/reports' },
    ],
  },
  validate: {
    id: 'validate',
    name: 'Validate',
    fullName: 'ThreatLens Validate',
    tagline: 'Continuous Exposure Validation',
    description: 'Safely validate exposures, prove remediation, and track validation history without aggressive or destructive testing.',
    color: '#8B5CF6',
    icon: 'ShieldAlert',
    defaultRoute: '/new/validate/jobs',
    comingSoon: false,
    tierRequired: null,
    navItems: [
      { type: 'subsection', label: 'Validation' },
      { icon: 'ShieldAlert', label: 'Validation Jobs',    path: '/new/validate/jobs' },
      { icon: 'Route',       label: 'Proof-of-Fix',       path: '/new/validate/proof-of-fix' },
      { icon: 'Settings',    label: 'Validation Policy',  path: '/new/validate/policy' },
      { icon: 'Activity',    label: 'Validation History', path: '/new/validate/history' },
    ],
  },
  workflow: {
    id: 'workflow',
    name: 'Workflow',
    fullName: 'ThreatLens Workflow',
    tagline: 'Ownership, Tickets, SLAs, and Integrations',
    description: 'Close the loop from exposure discovery to assigned work, validation, evidence packs, integrations, and client trust.',
    color: '#3B82F6',
    icon: 'Wrench',
    defaultRoute: '/new/workflow/findings',
    comingSoon: false,
    tierRequired: null,
    navItems: [
      { type: 'subsection', label: 'Remediation' },
      { icon: 'ShieldAlert', label: 'Unified Findings',   path: '/new/workflow/findings' },
      { icon: 'FileText',    label: 'Evidence Artifacts', path: '/new/workflow/evidence' },
      { icon: 'Wrench',      label: 'Remediation',        path: '/new/workflow/remediation' },
      { icon: 'Route',       label: 'Tickets',            path: '/new/workflow/tickets' },
      { icon: 'Settings',    label: 'SLAs & Exceptions',  path: '/new/workflow/policies' },
      { icon: 'Plug',        label: 'Trust Portal',       path: '/new/workflow/trust' },
    ],
  },
};

/** Ordered array for rendering product tabs */
export const PRODUCT_ORDER = ['surface', 'exposure', 'risk', 'validate', 'workflow'];

/**
 * Derive the active product from the current URL pathname.
 * Returns product ID string, or null for global pages (settings, support).
 */
export function getActiveProduct(pathname) {
  if (pathname.startsWith('/new/exposure')) return 'exposure';
  if (pathname.startsWith('/new/risk')) return 'risk';
  if (pathname.startsWith('/new/validate')) return 'validate';
  if (pathname.startsWith('/new/workflow')) return 'workflow';
  if (
    pathname.startsWith('/new/surface') ||
    pathname.startsWith('/new/dashboard') ||
    pathname.startsWith('/surface') ||
    pathname === '/dashboard' ||
    pathname === '/attack-paths' ||
    pathname === '/lensguard-ai'
  ) return 'surface';
  // Global pages (settings, support, integrations) — return null so the hook falls back to last active
  return null;
}
